import { Check, ChevronsUpDown, Lock } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  CUSTOM_TYPE_GROUP_ID,
  CUSTOM_TYPE_GROUP_TITLE,
  customTypeIdOf,
  customTypeLabel,
  customTypeValue,
  customWorkflowEnabled
} from "@/features/customCaseTypes/customCaseTypes";
import type { CustomCaseType } from "@/features/customCaseTypes/types";
import { cn } from "@/lib/utils";

import {
  RFE_VISA_CATEGORY_ID,
  VISA_CATEGORIES,
  visaOptionFor,
  withoutLoneVersionTags
} from "../../../components/common/visaOptions";
import { useContactSales } from "../../../hooks/useContactSales";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { Feature as PlanFeature } from "../../../types/entitlements";

export const visaLabel = (value: string | null | undefined): string => {
  if (!value) return "";
  const option = visaOptionFor(value);
  if (!option) return value;
  return option.tag ? `${option.label} (${option.tag})` : option.label;
};

const customTypeLabelOf = (
  types: readonly CustomCaseType[] | undefined,
  id: string
): string => {
  const type = types?.find((t) => t.id === id);
  return type ? customTypeLabel(type.name) : "";
};

type Props = {
  id?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Category ids to leave out (e.g. the RFE / NOID / NOIR workflow group). */
  excludeCategories?: string[];
  /** Visa values to leave out. */
  excludeVisas?: string[];
  /**
   * The firm's saved custom case types, offered as "Custom: <name>" under
   * Firm Custom where Custom Workflow is on; picked as `customTypeValue(id)`.
   */
  customTypes?: readonly CustomCaseType[];
  className?: string;
};

/**
 * Searchable visa / workflow picker grouped like the legacy `VisaTypeSelector`
 * (Popover + Command). The RFE / NOID / NOIR group is locked for firms whose
 * plan lacks RFE responses and links to sales instead.
 */
export const VisaPicker: FC<Props> = ({
  id,
  value,
  onChange,
  placeholder = "Select...",
  disabled,
  invalid,
  excludeCategories = [],
  excludeVisas = [],
  customTypes,
  className
}) => {
  const [open, setOpen] = useState(false);
  const { hasFeature } = useEntitlements();
  const { contactSales } = useContactSales();
  // Notice-response workflows are drafting work, so they ride on that plan feature.
  const rfeLocked = !hasFeature(PlanFeature.DRAFTING);

  const categories = useMemo(
    () =>
      VISA_CATEGORIES.filter((c) => !excludeCategories.includes(c.id))
        .map((c) => ({
          ...c,
          visas: withoutLoneVersionTags(
            c.visas.filter((v) => !excludeVisas.includes(v.value))
          )
        }))
        .concat(
          customTypes?.length && customWorkflowEnabled()
            ? [
                {
                  id: CUSTOM_TYPE_GROUP_ID,
                  title: CUSTOM_TYPE_GROUP_TITLE,
                  icon: undefined,
                  visas: customTypes.map((t) => ({
                    value: customTypeValue(t.id),
                    label: customTypeLabel(t.name)
                  }))
                }
              ]
            : []
        )
        .filter((c) => c.visas.length > 0),
    [excludeCategories, excludeVisas, customTypes]
  );
  const typeId = customTypeIdOf(value);
  const shown = typeId
    ? customTypeLabelOf(customTypes, typeId)
    : visaLabel(value);

  return (
    // `modal` so the list scrolls when the picker is opened inside a Sheet /
    // Dialog: without it the drawer's scroll lock swallows wheel events over
    // the portaled popover content.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            invalid && "border-destructive/60",
            className
          )}
        >
          <span className="truncate">{shown || placeholder}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0"
      >
        <Command>
          <CommandInput placeholder="Search visas…" className="h-8 text-xs" />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-xs">No visa found.</CommandEmpty>
            {categories.map((category) => {
              const locked = rfeLocked && category.id === RFE_VISA_CATEGORY_ID;
              return (
                <CommandGroup
                  key={category.id}
                  heading={category.title}
                  className="[&_[cmdk-group-heading]]:text-xs"
                >
                  {category.visas.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.value} ${
                        option.tag ?? ""
                      }`}
                      className={cn(
                        "text-xs",
                        locked && "text-muted-foreground"
                      )}
                      onSelect={() => {
                        setOpen(false);
                        if (locked) {
                          contactSales();
                          return;
                        }
                        onChange(option.value === value ? null : option.value);
                      }}
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                      {option.tag ? (
                        <span className="text-xs uppercase text-muted-foreground">
                          {option.tag}
                        </span>
                      ) : null}
                      {locked ? (
                        <Lock className="size-3 opacity-60" />
                      ) : option.value === value ? (
                        <Check className="size-3.5 opacity-70" />
                      ) : null}
                    </CommandItem>
                  ))}
                  {locked ? (
                    <div className="px-2 pb-1 text-xs text-muted-foreground">
                      Not included in your plan. Select to contact sales.
                    </div>
                  ) : null}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
